import { useEffect, useMemo, useRef, useState } from 'react';
import { journalEntries } from '@/utils/journalFeed';
import Reveal from '@/components/base/Reveal';

type DirectionFilter = 'ALL' | 'LONG' | 'SHORT';
type ResultFilter = 'ALL' | 'PROFIT' | 'LOSS';

interface SegmentedProps<T extends string> {
  ariaLabel: string;
  value: T;
  options: { key: T; label: string }[];
  onChange: (value: T) => void;
}

function Segmented<T extends string>({ ariaLabel, value, options, onChange }: SegmentedProps<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="flex flex-wrap items-center gap-1 p-1 rounded-lg border border-background-200 bg-background-50"
    >
      {options.map((opt) => {
        const active = opt.key === value;
        return (
          <button
            key={opt.key}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(opt.key)}
            className={`flex-1 min-w-[3rem] h-7 px-2 rounded-full text-[12px] font-medium whitespace-nowrap transition-colors cursor-pointer ${
              active
                ? 'bg-foreground-950 text-background-50'
                : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-950'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

const directionOptions: { key: DirectionFilter; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'LONG', label: 'Long' },
  { key: 'SHORT', label: 'Short' },
];

const resultOptions: { key: ResultFilter; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'PROFIT', label: 'Profit' },
  { key: 'LOSS', label: 'Loss' },
];

function toDirection(side: 'BUY' | 'SELL'): 'LONG' | 'SHORT' {
  return side === 'BUY' ? 'LONG' : 'SHORT';
}

// Date helpers. Entry dates look like 'SEP 21, 2026'; the DATE filter works off
// the year and month that each entry actually falls in, resolved from the data
// itself, so the buttons always reflect whatever the ledger currently holds.
const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatNet = (n: number): string =>
  `${n < 0 ? '-' : '+'}${Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const entryYearMonths = journalEntries.map((entry) => {
  const date = new Date(entry.date);
  return { year: String(date.getFullYear()), month: date.getMonth() + 1 };
});

const availableYears = Array.from(new Set(entryYearMonths.map((item) => item.year))).sort(
  (a, b) => Number(b) - Number(a),
);

function TradeTable() {
  const [direction, setDirection] = useState<DirectionFilter>('ALL');
  const [result, setResult] = useState<ResultFilter>('ALL');
  const [yearFilter, setYearFilter] = useState<string>('ALL');
  const [monthFilter, setMonthFilter] = useState<string>('ALL');
  const scrollRef = useRef<HTMLDivElement>(null);

  const yearOptions = useMemo(
    () => [
      { key: 'ALL', label: 'All' },
      ...availableYears.map((year) => ({ key: year, label: year })),
    ],
    [],
  );

  // Which months actually carry entries for the selected year. Every month is
  // always rendered as its own button so the strip fills the panel no matter how
  // few months the ledger holds yet; months with no recorded trades are simply
  // shown as inactive. Add trades in new months and they light up automatically.
  const availableMonths = useMemo(() => {
    const months = new Set<number>();
    entryYearMonths.forEach((item) => {
      if (yearFilter === 'ALL' || item.year === yearFilter) months.add(item.month);
    });
    return months;
  }, [yearFilter]);

  // If the selected month has no entries after switching year, fall back to All.
  useEffect(() => {
    if (monthFilter !== 'ALL' && !availableMonths.has(Number(monthFilter))) {
      setMonthFilter('ALL');
    }
  }, [availableMonths, monthFilter]);

  // True whenever any of the three groups is narrowed from its default, so we
  // know whether a one-click reset has anything to clear.
  const hasActiveFilter =
    direction !== 'ALL' || result !== 'ALL' || yearFilter !== 'ALL' || monthFilter !== 'ALL';

  const resetAll = () => {
    setDirection('ALL');
    setResult('ALL');
    setYearFilter('ALL');
    setMonthFilter('ALL');
  };

  // The feed is newest-first, so the top of the table is the most recent entry.
  // Land there every time the table is (re)opened or a filter changes, so the
  // reader always starts from the latest record rather than a stale position.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [direction, result, yearFilter, monthFilter]);

  const entries = useMemo(
    () =>
      journalEntries.filter((entry) => {
        const dir = toDirection(entry.side);
        if (direction !== 'ALL' && dir !== direction) return false;
        if (result === 'PROFIT' && !entry.profit) return false;
        if (result === 'LOSS' && entry.profit) return false;
        if (yearFilter !== 'ALL' || monthFilter !== 'ALL') {
          const date = new Date(entry.date);
          if (yearFilter !== 'ALL' && String(date.getFullYear()) !== yearFilter) return false;
          if (monthFilter !== 'ALL' && String(date.getMonth() + 1) !== monthFilter) return false;
        }
        return true;
      }),
    [direction, result, yearFilter, monthFilter],
  );

  // Human-readable label for the active date scope, e.g. 'Jul 2026', '2026' or 'Jul'.
  const scopeLabel = useMemo(() => {
    const monthName = monthFilter !== 'ALL' ? MONTH_LABELS[Number(monthFilter) - 1] : '';
    if (yearFilter !== 'ALL' && monthName) return `${monthName} ${yearFilter}`;
    if (yearFilter !== 'ALL') return yearFilter;
    return monthName;
  }, [yearFilter, monthFilter]);

  // Win/loss count and the net result across the rows currently shown, so the
  // summary always agrees with what the reader can actually see in the table.
  const scopeStats = useMemo(() => {
    let wins = 0;
    let losses = 0;
    let net = 0;
    entries.forEach((entry) => {
      if (entry.profit) wins += 1;
      else losses += 1;
      const value = Number(entry.result);
      if (!Number.isNaN(value)) net += value;
    });
    return { wins, losses, net };
  }, [entries]);

  // The newest record in the current view, resolved from the data itself (the
  // most recent date present) rather than a fixed row number. Update the trade
  // ledger with new entries and the "latest" highlight simply moves to whatever
  // the newest record now is — the visual treatment is preserved automatically.
  const latestDate = useMemo(() => {
    let best = '';
    let bestTime = -Infinity;
    entries.forEach((entry) => {
      const time = new Date(entry.date).getTime();
      if (!Number.isNaN(time) && time > bestTime) {
        bestTime = time;
        best = entry.date;
      }
    });
    return best;
  }, [entries]);

  return (
    <section
      id="journal-table"
      className="w-full border-y border-background-200 bg-background-100 px-4 md:px-8 lg:px-12 py-16 md:py-24"
    >
      <div className="max-w-[1440px] mx-auto">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <p className="text-[11px] font-data tracking-[0.24em] text-primary-600 mb-5">
                TRADE RECORD
              </p>
              <h2 className="font-heading font-extrabold tracking-tight text-foreground-950 text-[30px] leading-[1.08] sm:text-[40px] lg:text-[46px]">
                Recorded Entries
              </h2>
              <p className="mt-4 text-[14px] md:text-[15px] leading-relaxed text-foreground-600 max-w-[620px]">
                Filter the recorded activity by direction and outcome. The entries reflect the
                trades available in the current record.
              </p>
            </div>
          </div>
        </Reveal>

        {/* filters */}
        <Reveal delay={80}>
          <div className="mt-8 rounded-lg border border-background-300 bg-background-50 overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 md:px-5 py-2.5 border-b border-background-200 bg-background-100">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-2 text-[11px] font-data tracking-[0.18em] text-foreground-500">
                  <i className="ri-equalizer-line text-[14px] text-foreground-400"></i>
                  FILTERS
                </span>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-data tracking-[0.16em] text-foreground-400">
                  <i className="ri-calendar-line text-[12px]"></i>
                  {scopeLabel || 'All dates'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center h-7 rounded-full border border-background-300 bg-background-50 px-3 text-[12px] font-data tracking-wide text-foreground-600 whitespace-nowrap">
                  <span className="font-semibold text-foreground-950 mr-1">{entries.length}</span>
                  {entries.length === 1 ? 'entry' : 'entries'}
                </span>
                {hasActiveFilter && (
                  <button
                    type="button"
                    onClick={resetAll}
                    aria-label="Reset all filters"
                    className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full border border-background-300 bg-background-50 text-[12px] font-medium text-foreground-700 whitespace-nowrap transition-colors cursor-pointer hover:border-foreground-400 hover:text-foreground-950"
                  >
                    <i className="ri-refresh-line text-[13px]"></i>
                    Reset all
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 p-3 md:p-4">
              <div className="rounded-lg border border-background-200 bg-background-100 p-2.5">
                <span className="block text-[10px] font-data tracking-[0.18em] text-foreground-500 mb-2">
                  DIRECTION
                </span>
                <Segmented
                  ariaLabel="Filter by direction"
                  value={direction}
                  options={directionOptions}
                  onChange={setDirection}
                />
              </div>
              <div className="rounded-lg border border-background-200 bg-background-100 p-2.5">
                <span className="block text-[10px] font-data tracking-[0.18em] text-foreground-500 mb-2">
                  RESULT
                </span>
                <Segmented
                  ariaLabel="Filter by result"
                  value={result}
                  options={resultOptions}
                  onChange={setResult}
                />
              </div>
              <div className="rounded-lg border border-background-200 bg-background-100 p-2.5">
                <span className="block text-[10px] font-data tracking-[0.18em] text-foreground-500 mb-2">
                  YEAR
                </span>
                <Segmented
                  ariaLabel="Filter by year"
                  value={yearFilter}
                  options={yearOptions}
                  onChange={setYearFilter}
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-3 rounded-lg border border-background-200 bg-background-100 p-2.5">
                <span className="block text-[10px] font-data tracking-[0.18em] text-foreground-500 mb-2">
                  MONTH
                </span>
                <div
                  role="group"
                  aria-label="Filter by month"
                  className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-[repeat(13,minmax(0,1fr))] gap-1 p-1 rounded-lg border border-background-200 bg-background-50"
                >
                  <button
                    type="button"
                    aria-pressed={monthFilter === 'ALL'}
                    onClick={() => setMonthFilter('ALL')}
                    className={`h-7 rounded-full text-[12px] font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      monthFilter === 'ALL'
                        ? 'bg-foreground-950 text-background-50'
                        : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-950'
                    }`}
                  >
                    All
                  </button>
                  {MONTH_LABELS.map((label, idx) => {
                    const key = String(idx + 1);
                    const active = monthFilter === key;
                    const hasData = availableMonths.has(idx + 1);
                    return (
                      <button
                        key={key}
                        type="button"
                        aria-pressed={active}
                        disabled={!hasData}
                        onClick={() => setMonthFilter(key)}
                        className={`h-7 rounded-full text-[12px] font-medium whitespace-nowrap transition-colors ${
                          active
                            ? 'bg-foreground-950 text-background-50 cursor-pointer'
                            : hasData
                              ? 'text-foreground-600 hover:bg-background-100 hover:text-foreground-950 cursor-pointer'
                              : 'text-foreground-300 cursor-not-allowed'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* table */}
        <Reveal delay={140}>
          <div className="mt-5 rounded-lg border border-background-300 bg-background-50 overflow-hidden">
            {scopeLabel && (
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-3.5 border-b border-background-200 bg-background-100">
                <span className="text-[11px] font-data tracking-[0.18em] text-foreground-950">
                  {scopeLabel}
                </span>
                <span className="text-[12px] font-data text-foreground-600 whitespace-nowrap">
                  <span className="font-semibold text-profit-600">{scopeStats.wins}</span> win
                  {' / '}
                  <span className="font-semibold text-loss-500">{scopeStats.losses}</span> loss
                </span>
                <span className="text-[12px] font-data text-foreground-600 whitespace-nowrap">
                  Net
                  {' '}
                  <span
                    className={`font-semibold ${
                      scopeStats.net >= 0 ? 'text-profit-600' : 'text-loss-500'
                    }`}
                  >
                    {formatNet(scopeStats.net)}
                  </span>
                </span>
              </div>
            )}
            {entries.length > 0 ? (
              <div ref={scrollRef} className="overflow-auto max-h-[560px] journal-scroll">
                <table className="w-full min-w-[840px] border-collapse">
                  <thead>
                    <tr>
                      <th className="sticky top-0 z-10 bg-background-100 text-left text-[10px] font-data font-normal tracking-[0.16em] text-foreground-500 px-5 py-4 border-b border-background-200">
                        INSTRUMENT
                      </th>
                      <th className="sticky top-0 z-10 bg-background-100 text-left text-[10px] font-data font-normal tracking-[0.16em] text-foreground-500 px-5 py-4 border-b border-background-200">
                        DIRECTION
                      </th>
                      <th className="sticky top-0 z-10 bg-background-100 text-left text-[10px] font-data font-normal tracking-[0.16em] text-foreground-500 px-5 py-4 border-b border-background-200">
                        DATE
                      </th>
                      <th className="sticky top-0 z-10 bg-background-100 text-right text-[10px] font-data font-normal tracking-[0.16em] text-foreground-500 px-5 py-4 border-b border-background-200">
                        ENTRY
                      </th>
                      <th className="sticky top-0 z-10 bg-background-100 text-right text-[10px] font-data font-normal tracking-[0.16em] text-foreground-500 px-5 py-4 border-b border-background-200">
                        EXIT
                      </th>
                      <th className="sticky top-0 z-10 bg-background-100 text-right text-[10px] font-data font-normal tracking-[0.16em] text-foreground-500 px-5 py-4 border-b border-background-200">
                        VOLUME
                      </th>
                      <th className="sticky top-0 z-10 bg-background-100 text-right text-[10px] font-data font-normal tracking-[0.16em] text-foreground-500 px-5 py-4 border-b border-background-200">
                        RESULT
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry, idx) => {
                      const dir = toDirection(entry.side);
                      const isLatest = entry.date === latestDate;
                      return (
                        <tr
                          key={`${entry.date}-${entry.side}-${idx}`}
                          className={`border-b border-background-200 last:border-b-0 transition-colors ${
                            isLatest
                              ? 'bg-accent-100/60 hover:bg-accent-100'
                              : 'hover:bg-background-100/70'
                          }`}
                        >
                          <td
                            className={`px-5 py-4 font-data text-[13px] font-semibold text-foreground-950 whitespace-nowrap border-l-2 ${
                              isLatest ? 'border-accent-400' : 'border-transparent'
                            }`}
                          >
                            {entry.instrument}
                          </td>
                          <td className="px-5 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-100 px-2.5 py-1">
                              <i
                                className={
                                  dir === 'LONG'
                                    ? 'ri-arrow-up-line text-[13px] text-foreground-700'
                                    : 'ri-arrow-down-line text-[13px] text-foreground-700'
                                }
                              ></i>
                              <span className="text-[11px] font-data tracking-wider text-foreground-800">
                                {dir}
                              </span>
                            </span>
                          </td>
                          <td className="px-5 py-4 font-data text-[12px] tracking-wide text-foreground-500 whitespace-nowrap">
                            <span className="inline-flex items-center gap-2">
                              {isLatest && (
                                <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[9px] font-data tracking-[0.16em] text-accent-800">
                                  LATEST
                                </span>
                              )}
                              {entry.date}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-right font-data text-[13px] text-foreground-800 whitespace-nowrap">
                            {entry.entry}
                          </td>
                          <td className="px-5 py-4 text-right font-data text-[13px] text-foreground-800 whitespace-nowrap">
                            {entry.exit}
                          </td>
                          <td className="px-5 py-4 text-right font-data text-[13px] text-foreground-800 whitespace-nowrap">
                            {entry.volume}
                          </td>
                          <td
                            className={`px-5 py-4 text-right font-data text-[14px] font-semibold whitespace-nowrap ${
                              entry.profit ? 'text-profit-600' : 'text-loss-500'
                            }`}
                          >
                            {entry.result}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="px-6 py-16 text-center">
                <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-filter-off-line text-2xl"></i>
                </span>
                <p className="mt-4 text-[14px] font-medium text-foreground-800">
                  No entries match this filter.
                </p>
                <p className="mt-1 text-[13px] text-foreground-500">
                  Try adjusting the direction or result filter.
                </p>
              </div>
            )}
          </div>
        </Reveal>

        <Reveal delay={180}>
          <p className="mt-4 text-[11px] font-data tracking-wide text-foreground-400">
            ALL {entries.length} {entries.length === 1 ? 'ENTRY' : 'ENTRIES'} SHOWN — SCROLL WITHIN THE TABLE TO VIEW THE FULL RECORD.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export default TradeTable;